import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import Header from '../../layout/Header/Header';
import './SniperConfig.css';

const API_BASE_URL = import.meta.env.VITE_API_URL ;

/**
 * SniperConfig - Configuración del bot de Solana Sniper
 */
export default function SniperConfig() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  
  const [formData, setFormData] = useState({
    solanaPrivateKey: '',
    solanaPublicKey: '',
    buyAmountSol: 0.1,
    slippageBps: 200,
    takeProfit1Pct: 1.0,
    takeProfit2Pct: 4.0,
    stopLossBasePct: 0.3,
    maxDcaCount: 1,
    dcaTriggerPct: -0.05,
    buyThreshold: 7,
    executeTradesReal: true
  });
  
  useEffect(() => {
    // Validación: Solo Admin puede configurar
    if (user && user.tipo !== 'Admin') {
      navigate('/sniper'); // Redirigir a dashboard
      return;
    }
    fetchConfig();
  }, [user, navigate]);
  
  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('app_token');
      const res = await axios.get(`${API_BASE_URL}/bot/config/sniper`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("app_token")}` }
      });
      
      if (res.data.success && res.data.data) {
        setConfig(res.data.data);
        // No recibimos solanaPrivateKey por seguridad
        setFormData({
          ...res.data.data,
          solanaPrivateKey: '' // No llenar
        });
      }
    } catch (err) {
      console.error('Error fetching config:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    
    try {
      const token = localStorage.getItem('app_token');
      
      // Solo enviar private key si se cambió
      const dataToSend = { ...formData };
      if (!dataToSend.solanaPrivateKey) {
        delete dataToSend.solanaPrivateKey;
      }
      
      const res = await axios.post(`${API_BASE_URL}/bot/config/sniper`, dataToSend, {
        headers: { Authorization: `Bearer ${localStorage.getItem("app_token")}` }
      });
      
      if (res.data.success) {
        setConfig(res.data.data);
        setMessage({ type: 'success', text: 'Configuration saved successfully!' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.error || 'Error saving configuration' 
      });
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <>
        <Header />
        <div className="sniper-config loading">Loading configuration...</div>
      </>
    );
  }
  


  return (
    <>
      <Header />
      
      <div className="sniper-config">
        <div className="config-header">
          <button
            className="btn-back"
            onClick={() => navigate('/sniper')}
            title="Back to Dashboard"
          >
            ← Back
          </button>
          <div className="config-header-content">
            <h1>Solana Sniper Configuration</h1>
            <p className="config-subtitle">
              Configure your Solana whale tracking bot
            </p>
          </div>
        </div>
      
      {message && (
        <div className={`message message--${message.type}`}>
          {message.text}
        </div>
      )}
      
      <div className="config-warning-box">
        <h3>⚠️ Important</h3>
        <p>This bot follows whale wallets on Solana and automatically copies their trades with real money.</p>
        <p><strong>Make sure you understand the risks</strong> before enabling the bot.</p>
      </div>
      
      <form onSubmit={handleSubmit} className="config-form">
        {/* Solana Wallet */}
        <div className="form-section">
          <h3>🔑 Solana Wallet</h3>
          
          <div className="form-group">
            <label>
              Public Key
              <span className="label-help">Your Solana wallet address</span>
            </label>
            <input
              type="text"
              value={formData.solanaPublicKey}
              onChange={(e) => handleInputChange('solanaPublicKey', e.target.value)}
              placeholder="ABC123..."
              required
            />
          </div>
          
          <div className="form-group">
            <label>
              Private Key (Base58)
              <span className="label-help">Only needed if changing wallet</span>
            </label>
            <input
              type="password"
              value={formData.solanaPrivateKey}
              onChange={(e) => handleInputChange('solanaPrivateKey', e.target.value)}
              placeholder="Leave empty to keep current"
            />
            <p className="help-text warning">
              🔒 Your private key is encrypted before saving. Never share it with anyone.
            </p>
          </div>
        </div>
        
        {/* Trading Configuration */}
        <div className="form-section">
          <h3>💰 Trading Parameters</h3>
          
          <div className="form-group">
            <label>
              Buy Amount (SOL)
              <span className="label-help">Amount to invest per trade</span>
            </label>
            <div className="input-with-addon">
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={formData.buyAmountSol}
                onChange={(e) => handleInputChange('buyAmountSol', parseFloat(e.target.value))}
                required
              />
              <span className="addon">SOL</span>
            </div>
          </div>
          
          <div className="form-group">
            <label>
              Slippage Tolerance
              <span className="label-help">Maximum price slippage allowed</span>
            </label>
            <div className="input-with-addon">
              <input
                type="number"
                min="50"
                max="5000"
                step="50"
                value={formData.slippageBps}
                onChange={(e) => handleInputChange('slippageBps', parseInt(e.target.value))}
              />
              <span className="addon">{(formData.slippageBps / 100).toFixed(1)}%</span>
            </div>
          </div>
          
          <div className="form-group">
            <label>
              Buy Threshold (Score)
              <span className="label-help">Minimum whale score to trigger buy</span>
            </label>
            <input
              type="number"
              min="1"
              max="20"
              step="1"
              value={formData.buyThreshold}
              onChange={(e) => handleInputChange('buyThreshold', parseInt(e.target.value))}
            />
            <p className="help-text">
              Whales have scores from 8-12. Higher threshold = more selective.
            </p>
          </div>
        </div>
        
        {/* Exit Strategy */}
        <div className="form-section">
          <h3>📈 Exit Strategy</h3>
          
          <div className="form-group">
            <label>Take Profit 1 (Sell 50%)</label>
            <div className="input-with-addon">
              <input
                type="number"
                min="0.1"
                max="10"
                step="0.1"
                value={formData.takeProfit1Pct}
                onChange={(e) => handleInputChange('takeProfit1Pct', parseFloat(e.target.value))}
              />
              <span className="addon">{(formData.takeProfit1Pct * 100).toFixed(0)}% = {(formData.takeProfit1Pct + 1).toFixed(1)}x</span>
            </div>
          </div>
          
          <div className="form-group">
            <label>Take Profit 2 (Sell 100%)</label>
            <div className="input-with-addon">
              <input
                type="number"
                min="0.1"
                max="20"
                step="0.1"
                value={formData.takeProfit2Pct}
                onChange={(e) => handleInputChange('takeProfit2Pct', parseFloat(e.target.value))}
              />
              <span className="addon">{(formData.takeProfit2Pct * 100).toFixed(0)}% = {(formData.takeProfit2Pct + 1).toFixed(1)}x</span>
            </div>
          </div>
          
          <div className="form-group">
            <label>Base Stop Loss</label>
            <div className="input-with-addon">
              <input
                type="number"
                min="0.1"
                max="0.9"
                step="0.05"
                value={formData.stopLossBasePct}
                onChange={(e) => handleInputChange('stopLossBasePct', parseFloat(e.target.value))}
              />
              <span className="addon">{(formData.stopLossBasePct * 100).toFixed(0)}%</span>
            </div>
            <p className="help-text">
              Actual SL is dynamic based on whale quality (30-60%)
            </p>
          </div>
        </div>
        
        {/* DCA */}
        <div className="form-section">
          <h3>📉 DCA (Dollar Cost Averaging)</h3>
          
          <div className="form-group">
            <label>Max DCA Count</label>
            <input
              type="number"
              min="0"
              max="5"
              value={formData.maxDcaCount}
              onChange={(e) => handleInputChange('maxDcaCount', parseInt(e.target.value))}
            />
            <p className="help-text">
              How many times to average down if price drops
            </p>
          </div>
          
          <div className="form-group">
            <label>DCA Trigger</label>
            <div className="input-with-addon">
              <input
                type="number"
                min="-0.5"
                max="0"
                step="0.01"
                value={formData.dcaTriggerPct}
                onChange={(e) => handleInputChange('dcaTriggerPct', parseFloat(e.target.value))}
              />
              <span className="addon">{(formData.dcaTriggerPct * 100).toFixed(0)}%</span>
            </div>
            <p className="help-text">
              Trigger DCA when price drops by this percentage
            </p>
          </div>
        </div>
        
        {/* Submit */}
        <div className="form-actions">
          <button 
            type="submit" 
            className="btn-save"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
          
          <button 
            type="button" 
            className="btn-cancel"
            onClick={fetchConfig}
          >
            Reset
          </button>
        </div>
      </form>
      </div>
    </>
  );
}
